import React from 'react';
import { useAtomValue } from 'jotai';
import { Filters } from '@rocco/ui/filter-sort/components/Filters';
import { FilterTabs } from '@rocco/ui/filter-sort/components/filters-bar/FilterTabs';
import { FilterSeparator } from '@rocco/ui/filter-sort/components/filters-bar/FilterSeparator';
import type { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { searchPageDataAtom } from '../states/search-page-data-atom';
import { getFilterSettings } from '../util/get-filter-settings';

interface SearchPageFilterTabsProps
    extends ReturnType<typeof useCategoryFilterSort> {}

/**
 * Renders the filter bar section, including filter tabs and potentially separator and other filter controls.
 * Adapts layout for mobile and desktop views.
 */
export const SearchPageFilterTabs: React.FC<SearchPageFilterTabsProps> = (
    props
) => {
    const pageDataFromAtom = useAtomValue(searchPageDataAtom);
    const filterTabs = pageDataFromAtom?.filterTabs ?? [];
    const searchIndex = pageDataFromAtom?.searchIndex;
    const appDisplayModeFromCtx = pageDataFromAtom?.appDisplayMode;
    const isMobile = appDisplayModeFromCtx === 'mobile';

    const filterSettings = getFilterSettings(searchIndex);
    const hasBarFilters = (filterSettings?.config?.barFilters?.length ?? 0) > 0;

    // Ensure displayMode passed to Filters is always valid
    const displayModeForFilters = appDisplayModeFromCtx || 'desktop';

    const filterProps = {
        ...props,
        displayMode: displayModeForFilters,
    };

    if (!pageDataFromAtom) {
        // Render nothing or a placeholder if essential data isn't loaded
        return null;
    }

    return !isMobile ? (
        <div className="sticky top-0 z-sticky mt-10 hidden overflow-x-auto bg-background md:mt-0 md:block">
            <div className="flex h-20 items-center pl-6 md:pl-0">
                <Filters {...filterProps}>
                    {filterTabs.length > 0 && (
                        <>
                            <FilterTabs
                                tabs={filterTabs}
                                displayMode={displayModeForFilters}
                            />
                            {hasBarFilters && <FilterSeparator />}
                        </>
                    )}
                </Filters>
            </div>
        </div>
    ) : (
        <div className="scrollbar-hide sticky top-0 z-sticky overflow-x-auto bg-background py-7 pl-6 pr-0 md:hidden">
            <Filters {...filterProps}>
                {filterTabs.length > 0 && (
                    <FilterTabs
                        tabs={filterTabs}
                        displayMode={displayModeForFilters}
                    />
                )}
            </Filters>
        </div>
    );
};
